/* globals fetchVenuesUrl fetchVenueRevenuesUrl fetchVenueExpensesUrl vikEnabled workforceEnabled sportAllocationStoreUrl dayTagStoreUrl dayTagBulkUrl venueBulkPreviewUrl venueBulkApplyUrl reEventId costTemplateEnabled costPackEnabled */

import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';
import RESummaryCards from 'Components/revenues_expenditures/RESummaryCards';
import RETypeIconLegend from 'Components/revenues_expenditures/RETypeIconLegend';
import RevenuesExpendituresTable from 'Components/revenues_expenditures/RevenuesExpendituresTable';

import { ConfirmationModalProvider } from 'Context/ConfirmationModalContext';

import useApiRequest from 'Hooks/useApiRequest';
import { useToast } from 'Hooks/useToast';

export default function VenueBudget(props) {
    const [venues, setVenues] = useState([]);
    const [selectedVenue, setSelectedVenue] = useState(null);

    const toast = useToast();

    const { data: venueRevenues, get: fetchVenueRevenues, loading: loadingRevenues } = useApiRequest({ url: props.fetchVenueRevenuesUrl });
    const { data: venueExpenses, get: fetchVenueExpenses, loading: loadingExpenses } = useApiRequest({ url: props.fetchVenueExpensesUrl });

    // Sports list — comes back in whichever API response arrives first.
    const sports = venueRevenues?.sports ?? venueExpenses?.sports ?? [];

    useEffect(() => {
        if (props.currentVenue) {
            setSelectedVenue(props.currentVenue);
            return;
        }

        fetchVenues();
    }, []);

    useEffect(() => {
        if (selectedVenue) {
            const replace = { VENUE_SLUG: selectedVenue.slug };
            fetchVenueRevenues(null, replace);
            fetchVenueExpenses(null, replace);
        }
    }, [selectedVenue]);

    const fetchVenues = () => {
        axios
            .get(props.fetchVenuesUrl)
            .then(({ data }) => {
                setVenues(data);
                setSelectedVenue(data[0]);
            })
            .catch(() => toast.error($t('An error occurred, please try again')));
    };

    const refetchRevenues = () => {
        if (!selectedVenue) return;
        fetchVenueRevenues(null, { VENUE_SLUG: selectedVenue.slug });
    };

    const refetchExpenses = () => {
        if (!selectedVenue) return;
        fetchVenueExpenses(null, { VENUE_SLUG: selectedVenue.slug });
    };

    return (
        <ConfirmationModalProvider>
            <div>
                {!props.currentVenue && (
                    <div className="tw-py-3 tw-flex tw-items-baseline tw-gap-x-4">
                        <h2 className="tw-text-3xl tw-font-semibold">{$t('Revenue & Expenditure by Venue')}</h2>

                        {!!venues?.length && (
                            <label className="tw-inline-flex tw-items-center tw-space-x-2 tw-text-sm">
                                <span className="tw-text-charcoal-75">{$t('Venue')}:</span>
                                <select
                                    className="tw-font-semibold tw-p-0 tw-border-0 focus:tw-ring-0 tw-min"
                                    style={{ width: selectedVenue ? Math.max(selectedVenue.name.length * 9, 128) : 128 }}
                                    value={selectedVenue?.id || ''}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        const venue = venues.find((v) => v.id == value);
                                        setSelectedVenue(venue);
                                    }}
                                >
                                    {venues.map(({ id, name }) => (
                                        <option key={id} value={id}>
                                            {name}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        )}
                    </div>
                )}

                <div className="tw-space-y-3 tw-mt-6">
                    <RESummaryCards
                        context="venue"
                        revenues={venueRevenues}
                        expenses={venueExpenses}
                        loadingRevenues={loadingRevenues}
                        loadingExpenses={loadingExpenses}
                    />

                    <RevenuesExpendituresTable
                        title={$t('Revenue')}
                        context="venue"
                        isRevenue
                        eventId={props.eventId}
                        loading={loadingRevenues}
                        nodes={venueRevenues?.tree || []}
                        totals={venueRevenues?.totals || null}
                        vikEnabled={props.vikEnabled}
                        sports={sports}
                        sportAllocationStoreUrl={props.sportAllocationStoreUrl}
                        venueId={selectedVenue?.id}
                        onAllocationUpdated={refetchRevenues}
                        paginate
                        totalCount={venueRevenues?.revenues_count || 0}
                        defaultExpandLevelZero
                    />

                    <RevenuesExpendituresTable
                        title={$t('Expenditure')}
                        context="venue"
                        eventId={props.eventId}
                        loading={loadingExpenses}
                        nodes={venueExpenses?.tree || []}
                        totals={venueExpenses?.totals || null}
                        vikEnabled={props.vikEnabled}
                        workforceEnabled={props.workforceEnabled}
                        sports={sports}
                        sportAllocationStoreUrl={props.sportAllocationStoreUrl}
                        dayTagStoreUrl={props.dayTagStoreUrl}
                        dayTagBulkUrl={props.dayTagBulkUrl}
                        bulkPreviewUrl={props.bulkPreviewUrl}
                        bulkApplyUrl={props.bulkApplyUrl}
                        venueId={selectedVenue?.id}
                        onAllocationUpdated={refetchExpenses}
                        commitmentLayerEnabled
                        committedTotals={venueExpenses?.committed_totals ?? null}
                        paginate
                        totalCount={venueExpenses?.expenses_count || 0}
                        pinGroupHeaders
                    />

                    <RETypeIconLegend showTemplateKey={props.costTemplateEnabled} showPackKey={props.costPackEnabled} />
                </div>
            </div>
        </ConfirmationModalProvider>
    );
}

if (document.getElementById('VenueBudget')) {
    var rootEl = document.getElementById('VenueBudget');
    ReactDOM.render(
        <App>
            <VenueBudget
                fetchVenuesUrl={fetchVenuesUrl}
                fetchVenueRevenuesUrl={fetchVenueRevenuesUrl}
                fetchVenueExpensesUrl={fetchVenueExpensesUrl}
                currentVenue={window.currentVenue || null}
                eventId={typeof reEventId !== 'undefined' ? reEventId : null}
                vikEnabled={vikEnabled}
                workforceEnabled={workforceEnabled}
                costTemplateEnabled={typeof costTemplateEnabled !== 'undefined' ? costTemplateEnabled : false}
                costPackEnabled={typeof costPackEnabled !== 'undefined' ? costPackEnabled : false}
                sportAllocationStoreUrl={sportAllocationStoreUrl}
                dayTagStoreUrl={dayTagStoreUrl}
                dayTagBulkUrl={dayTagBulkUrl}
                bulkPreviewUrl={typeof venueBulkPreviewUrl !== 'undefined' ? venueBulkPreviewUrl : null}
                bulkApplyUrl={typeof venueBulkApplyUrl !== 'undefined' ? venueBulkApplyUrl : null}
            />
        </App>,
        rootEl,
    );
}
